import React, { useCallback, useMemo } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import { Container, useResponsive } from '@sg/garnish';

import { GiftCard, useRedeemGiftCardDefaultValues } from '@order/features-gift-card';

import { AccountScreenTitle, SignedOutView, TransparentModal } from '../../components';
import { useIsLoggedIn } from '../../context/AuthMachine';
import { useCustomerCredit } from '../../hooks';
import { addGiftCardScreenMessages as messages } from './AddGiftCardScreen.messages';

// ─────────────────────────────────────────────────────────────────────────────

export const AddGiftCardScreen = () => {
  const { formatMessage } = useIntl();
  const router = useRouter();
  const params = useLocalSearchParams<{ giftCardId?: string }>();

  const { match, minWidth } = useResponsive();
  const containerStyles = match([styles.containerXS, styles.containerSM]);
  const formStyles = match([styles.formContainerXS, styles.formContainerSM]);

  const giftCardRedemptionEncodedData = params?.giftCardId;
  const defaultRedemptionValues = useRedeemGiftCardDefaultValues({
    giftCardRedemptionEncodedData,
  });

  const isLoggedIn = useIsLoggedIn();

  const { refetchCredits } = useCustomerCredit({
    requestPolicy: 'cache-and-network',
  });

  // ─── Derived Data ────────────────────────────────────────────────────

  const redirectTo = useMemo(() => {
    const giftCardId = params?.giftCardId;
    const basePath = minWidth.isSM ? '/account/gift-cards' : '/add-gift-card';

    return giftCardId ? `${basePath}?giftCardId=${encodeURIComponent(giftCardId)}` : basePath;
  }, [minWidth.isSM, params?.giftCardId]);

  // ─────────────────────────────────────────────────────────────────────

  const screenTitle = formatMessage(messages.title);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const handleClose = useCallback(() => {
    router.back();
  }, [router]);

  const onRedeemSuccess = useCallback(() => {
    refetchCredits();

    router.dismissTo('/account/gift-cards');
  }, [router, refetchCredits]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isLoggedIn) {
    return <SignedOutView redirectTo={redirectTo} withoutFooter />;
  }

  return (
    <TransparentModal size="full" onClose={handleClose} disableAnimation>
      <ScrollView>
        <Container wrapperStyle={containerStyles} style={containerStyles}>
          <AccountScreenTitle title={screenTitle} />

          <View style={formStyles}>
            <GiftCard.RedemptionForm
              style={styles.withoutBorder}
              giftCardCode={defaultRedemptionValues?.giftCardCode}
              giftCardRegistrationCode={defaultRedemptionValues?.giftCardRegistrationCode} // prettier-ignore
              onRedeemSuccess={onRedeemSuccess}
            />
          </View>
        </Container>
      </ScrollView>
    </TransparentModal>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  containerSM: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
    paddingVertical: theme.spacing['8'],
  },
  formContainerXS: {
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
  },
  formContainerSM: {
    paddingVertical: theme.spacing['8'],
  },
  withoutBorder: {
    padding: 0,
    borderWidth: 0,
  },
});
